import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";
import { Seo } from "../components/Seo";
import { PROJECT_TYPES, BUDGET_RANGES, CONTACT } from "../data";

const EMPTY = {
  name: "", email: "", phone: "", address: "",
  project_type: "", description: "", budget_range: "",
};

const inputClass =
  "rounded-none border-[#2B2B2B]/20 bg-white h-12 text-[#2B2B2B] focus-visible:ring-[#C68B4E] focus-visible:ring-offset-0 focus-visible:border-[#C68B4E]";

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSelect = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Quote request sent! We'll be in touch within 1 business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Get a Free Quote | Bright Oak Renovations"
        description="Request a free, no-obligation renovation quote from Bright Oak Renovations. Serving Cambridge, Kitchener, Waterloo & Guelph, Ontario."
        path="/contact"
      />
      <section className="bg-[#2B2B2B] text-[#F7F3EC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
          <p className="section-label">Request a Quote</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mt-4 max-w-3xl">
            Tell us about your project
          </h1>
          <p className="text-[#F7F3EC]/70 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
            Share a few details and we'll get back to you within one business day with next steps.
            Free, no-obligation consultations.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Contact info */}
          <div className="space-y-6">
            {[
              { icon: Phone, label: "Call", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}` },
              { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
              { icon: MapPin, label: "Location", value: CONTACT.address, href: null },
            ].map((c) => (
              <div key={c.label} className="bg-white border border-[#2B2B2B]/10 p-6 flex items-start gap-4">
                <span className="w-11 h-11 bg-[#7A8B6F]/15 flex items-center justify-center shrink-0">
                  <c.icon size={20} strokeWidth={1.5} className="text-[#7A8B6F]" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#C68B4E] font-bold">{c.label}</div>
                  {c.href ? (
                    <a href={c.href} className="font-semibold text-sm mt-1 block hover:text-[#C68B4E] transition-colors break-words">{c.value}</a>
                  ) : (
                    <div className="font-semibold text-sm mt-1">{c.value}</div>
                  )}
                </div>
              </div>
            ))}
            <div className="bg-[#7A8B6F]/10 border-l-4 border-[#7A8B6F] p-6">
              <p className="text-sm text-[#5C5C5C] leading-relaxed">
                <strong className="text-[#2B2B2B]">Business hours:</strong><br />
                Mon–Fri: 8:00am – 6:00pm<br />
                Sat: 9:00am – 2:00pm
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            {done ? (
              <div className="bg-white border border-[#2B2B2B]/10 p-10 lg:p-16 text-center" data-testid="quote-success">
                <CheckCircle2 size={56} className="text-[#7A8B6F] mx-auto" strokeWidth={1.5} />
                <h2 className="text-2xl font-extrabold tracking-tight mt-6">Thank you!</h2>
                <p className="text-[#5C5C5C] mt-3 max-w-md mx-auto leading-relaxed">
                  Your quote request has been received. A member of the Bright Oak team will reach out
                  within one business day.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="new-request-button"
                  className="mt-8 border-2 border-[#2B2B2B] text-[#2B2B2B] hover:bg-[#2B2B2B] hover:text-[#F7F3EC] transition-colors px-8 py-3 text-sm font-bold tracking-wider uppercase"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-[#2B2B2B]/10 p-8 lg:p-10 space-y-6" data-testid="quote-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs uppercase tracking-widest font-bold">Full Name *</Label>
                    <Input id="name" required value={form.name} onChange={set("name")} className={inputClass} placeholder="Jane Doe" data-testid="quote-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-widest font-bold">Email *</Label>
                    <Input id="email" type="email" required value={form.email} onChange={set("email")} className={inputClass} placeholder="jane@email.com" data-testid="quote-email-input" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs uppercase tracking-widest font-bold">Phone *</Label>
                    <Input id="phone" required value={form.phone} onChange={set("phone")} className={inputClass} placeholder="(416) 720-9886" data-testid="quote-phone-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-xs uppercase tracking-widest font-bold">Project Address *</Label>
                    <Input id="address" required value={form.address} onChange={set("address")} className={inputClass} placeholder="Street, City" data-testid="quote-address-input" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest font-bold">Project Type *</Label>
                    <Select value={form.project_type} onValueChange={setSelect("project_type")} required>
                      <SelectTrigger className={`${inputClass} data-[placeholder]:text-[#2B2B2B]/40`} data-testid="quote-project-type-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {PROJECT_TYPES.map((t) => (
                          <SelectItem key={t} value={t} className="rounded-none" data-testid={`project-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest font-bold">Budget Range *</Label>
                    <Select value={form.budget_range} onValueChange={setSelect("budget_range")} required>
                      <SelectTrigger className={`${inputClass} data-[placeholder]:text-[#2B2B2B]/40`} data-testid="quote-budget-select">
                        <SelectValue placeholder="Select a range" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {BUDGET_RANGES.map((b) => (
                          <SelectItem key={b} value={b} className="rounded-none">{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-xs uppercase tracking-widest font-bold">Project Description *</Label>
                  <Textarea id="description" required value={form.description} onChange={set("description")} rows={5}
                    className="rounded-none border-[#2B2B2B]/20 bg-white text-[#2B2B2B] focus-visible:ring-[#C68B4E] focus-visible:ring-offset-0 focus-visible:border-[#C68B4E] resize-none"
                    placeholder="Tell us about your vision, timeline, and any specifics..." data-testid="quote-description-input" />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit-button"
                  className="bg-[#C68B4E] text-white hover:bg-[#B37A3D] transition-colors px-10 py-4 text-sm font-bold tracking-wider uppercase inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Request My Free Quote"}
                  {!submitting && <Send size={17} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
